// pip-store — estado global do Picture-in-Picture do live player.
//
// Quem abre: botão "Espiar" no toast de lembrete (ReminderToastGlobal).
// Quem renderiza: <LivePip> em AppProviders (mesmo nível do toast).
// Quem fecha: user (botão X), expand→player (substitui PiP por full),
// ou auto-close por inatividade.
//
// Estado em memória só — PiP é efêmero por design, não persiste entre
// recargas. Se user F5, perde o PiP (que tava temporário mesmo).
//
// ===== Auto-close por tempo (decisão Fernando 2026-05-15) =====
//
// Pra evitar "PiP esquecido" drenando banda/CPU em segundo plano,
// limite duro de 20min total com 1 chance de estender:
//
//   - 0-9min:    PiP normal
//   - 9min:      banner "fechando em 60s" + botão "Manter aberto"
//   - 10min:     se user clicou "Manter" → estende +10min (e perde
//                opção de estender de novo). Se não → fecha.
//   - 19min:     (se estendido) banner "fechando em 60s" SEM botão
//   - 20min:     fecha sem alternativa. User reabre se quiser.
//
// Timer fixo (NÃO reseta com interação) — Fernando: "isso não
// soluciona o usuário ficar com o PiP eternamente". Limite duro
// garante teto independente de cliques.

import type { GroupedChannel } from '@app-iptv/iptv-client'
import { create } from 'zustand'

const FIRST_WARN_MS = 9 * 60 * 1000   //  9min — aparece banner
const FIRST_CLOSE_MS = 10 * 60 * 1000 // 10min — fecha (ou estende se user clicou)
const SECOND_WARN_MS = 19 * 60 * 1000 // 19min — banner sem botão
const SECOND_CLOSE_MS = 20 * 60 * 1000 // 20min — fecha sem alternativa

interface PipState {
  channelId: string | null
  group: GroupedChannel | null
  /** Quando o PiP foi aberto (ms epoch). null = fechado. */
  openedAt: number | null
  /** True se user já clicou "Manter aberto" 1×. Não pode estender de novo. */
  extended: boolean
  /** Audio mutado por default (Fernando decisão). */
  muted: boolean

  // Tick que atualiza a cada 1s pra UI poder mostrar countdown.
  // Componente que precisa do countdown subscreve a este store —
  // re-renderiza no tick.
  tick: number
}

interface PipActions {
  open: (channelId: string, group: GroupedChannel) => void
  close: () => void
  toggleMute: () => void
  /** User clicou "Manter aberto" no 1º aviso (9min). Estende prazo +10min. */
  keepOpen: () => void
  /** Internal — chamado pelo tick do componente <LivePip>. */
  _internalTick: () => void
}

export const usePipStore = create<PipState & PipActions>((set, get) => ({
  channelId: null,
  group: null,
  openedAt: null,
  extended: false,
  muted: true,
  tick: 0,

  open: (channelId, group) =>
    set({ channelId, group, openedAt: Date.now(), extended: false, muted: true }),

  close: () =>
    set({ channelId: null, group: null, openedAt: null, extended: false }),

  toggleMute: () => set((s) => ({ muted: !s.muted })),

  keepOpen: () => {
    // Reseta o openedAt pra "agora menos 10min" → começa o ciclo do
    // 2º bloco (warn em 19min total = 9min do extend).
    set({ openedAt: Date.now() - FIRST_CLOSE_MS, extended: true })
  },

  _internalTick: () => {
    const { openedAt } = get()
    if (openedAt === null) return
    const elapsed = Date.now() - openedAt
    if (elapsed >= SECOND_CLOSE_MS) {
      // 20min — fecha sem alternativa
      get().close()
      return
    }
    set((s) => ({ tick: s.tick + 1 }))
  },
}))

/** Estado derivado pra UI saber o que renderizar no PiP overlay. */
export type PipWarnPhase =
  | 'none' // sem aviso ainda
  | 'first-warn' // 9-10min: banner com botão "Manter aberto"
  | 'second-warn' // 19-20min: banner sem botão (já estendeu, agora vai fechar)

export function getWarnPhase(openedAt: number | null, extended: boolean): {
  phase: PipWarnPhase
  /** Segundos até o close (0 quando passou). */
  secondsUntilClose: number
} {
  if (openedAt === null) return { phase: 'none', secondsUntilClose: 0 }
  const elapsed = Date.now() - openedAt

  if (!extended) {
    // 1º bloco: 0-10min
    if (elapsed >= FIRST_WARN_MS && elapsed < FIRST_CLOSE_MS) {
      return {
        phase: 'first-warn',
        secondsUntilClose: Math.max(0, Math.ceil((FIRST_CLOSE_MS - elapsed) / 1000)),
      }
    }
    return { phase: 'none', secondsUntilClose: 0 }
  }

  // 2º bloco: 10-20min
  if (elapsed >= SECOND_WARN_MS && elapsed < SECOND_CLOSE_MS) {
    return {
      phase: 'second-warn',
      secondsUntilClose: Math.max(0, Math.ceil((SECOND_CLOSE_MS - elapsed) / 1000)),
    }
  }
  return { phase: 'none', secondsUntilClose: 0 }
}
